/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { AnyAction, createSlice } from '@reduxjs/toolkit';
import _ from 'lodash';

import { ApiStatus } from '../../../../ts';
import { EnvironmentType } from '../../../../ts/assistant';
import { AssistantsSlice } from '../../../../ts/state';
import { SET_TENANT } from '../session/actionTypes';
import { assignSnapshot, swapSnapshot } from '../snapshots/actions';
import {
  createAssistant,
  deleteAssistant,
  getAssistant,
  listAssistants,
  updateAssistant
} from './actions';
import { entityAdapter } from './adapter';

// Initial State
const initialState = entityAdapter.getInitialState({
  getAssistantStatus: ApiStatus.IDLE,
  getAssistantError: null,
  pendingGetStatus: {},

  listStatus: ApiStatus.IDLE, // idle, pending, fulfilled, error
  listFetched: null,
  listError: null,

  createStatus: ApiStatus.IDLE,
  createError: null,

  updateStatus: ApiStatus.IDLE,
  updateError: null,

  deleteStatus: ApiStatus.IDLE,
  deleteError: null
});

/**
 * Gets the initial state of the redux slice and apply updates.
 * @param {*} updates The updates to apply to the initial state
 * @returns {*} The slice initial state
 */
export const computeState = (updates?: any): AssistantsSlice => ({
  ..._.cloneDeep(initialState),
  ...updates
});

// Slice + Reducer
const slice = createSlice({
  name: 'assistants',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getAssistant.pending, (state, action) => {
        state.getAssistantStatus = ApiStatus.LOADING;
        state.getAssistantError = null;
        state.pendingGetStatus[action.meta.arg.assistantId] = Date.now();
      })
      .addCase(getAssistant.fulfilled, (state, action) => {
        state.getAssistantStatus = ApiStatus.SUCCEEDED;
        state.getAssistantError = null;
        delete state.pendingGetStatus[action.meta.arg.assistantId];
        entityAdapter.upsertOne(state, action.payload);
      })
      .addCase(getAssistant.rejected, (state, action) => {
        state.getAssistantStatus = ApiStatus.ERROR;
        state.getAssistantError = action.error;
        delete state.pendingGetStatus[action.meta.arg.assistantId];
      })
      .addCase(listAssistants.pending, (state, _action) => {
        state.listStatus = ApiStatus.LOADING;
        state.listError = null;
      })
      .addCase(listAssistants.fulfilled, (state, action) => {
        state.listStatus = ApiStatus.SUCCEEDED;
        state.listFetched = Date.now();
        state.listError = null;
        entityAdapter.setAll(state, action.payload);
      })
      .addCase(listAssistants.rejected, (state, action) => {
        state.listStatus = ApiStatus.ERROR;
        state.listError = action.error;
      })
      .addCase(createAssistant.pending, (state, _action) => {
        state.createStatus = ApiStatus.LOADING;
        state.createError = null;
      })
      .addCase(createAssistant.fulfilled, (state, action) => {
        state.createStatus = ApiStatus.SUCCEEDED;
        state.createError = null;
        entityAdapter.addOne(state, action.payload);
      })
      .addCase(createAssistant.rejected, (state, action) => {
        state.createStatus = ApiStatus.ERROR;
        state.createError = action.error;
      })
      .addCase(updateAssistant.pending, (state, _action) => {
        state.updateStatus = ApiStatus.LOADING;
        state.updateError = null;
      })
      .addCase(updateAssistant.fulfilled, (state, action) => {
        state.updateStatus = ApiStatus.SUCCEEDED;
        state.updateError = null;
        // FIXME: We need this so that we don't add environments to Assistant slice when updating an environment, see https://github.ibm.com/watson-engagement-advisor/wea-backlog/issues/56955
        // FIXME: This will go away after new store changes, cause we will not be using updateAssistant to update environments anymore
        // FIXME: See https://github.ibm.com/watson-engagement-advisor/wea-backlog/issues/56589
        if (
          !('environment_id' in action.payload) &&
          action.payload.environment !== EnvironmentType.STAGING &&
          action.payload.environment !== EnvironmentType.LIVE
        ) {
          entityAdapter.upsertOne(state, action.payload);
        }
      })
      .addCase(updateAssistant.rejected, (state, action) => {
        state.updateStatus = ApiStatus.ERROR;
        state.updateError = action.error;
      })
      .addCase(deleteAssistant.pending, (state, _action) => {
        state.deleteStatus = ApiStatus.LOADING;
        state.deleteError = null;
      })
      .addCase(deleteAssistant.fulfilled, (state, action) => {
        state.deleteStatus = ApiStatus.SUCCEEDED;
        state.deleteError = null;
        state.listFetched = null;
        entityAdapter.removeOne(state, action.payload);
      })
      .addCase(deleteAssistant.rejected, (state, action) => {
        state.deleteStatus = ApiStatus.ERROR;
        state.deleteError = action.error;
      })
      .addCase(swapSnapshot.fulfilled, (state, action) => {
        // During an snapshot assignment
        // update the skill reference of the assistant
        const assistant = state.entities[action.meta.arg.assistantId];
        const ref = action.payload;

        // FIXME: This needs to be handled in issue #56589.
        if (assistant) {
          assistant.skill_references = assistant.skill_references.filter(
            sk => sk.skill_reference !== ref.skill_reference
          );
          assistant.skill_references.push(ref);
        }
      })
      .addCase(assignSnapshot.fulfilled, (state, action) => {
        // During an snapshot assignment
        // update the skill reference of the assistant
        const assistant = state.entities[action.meta.arg.assistantId];
        const ref = action.payload;

        // FIXME: This needs to be handled in issue #56589.
        if (assistant) {
          assistant.skill_references = assistant.skill_references.filter(
            sk => sk.skill_reference !== ref.skill_reference
          );
          assistant.skill_references.push(ref);
        }
      })
      .addCase<string, AnyAction>(SET_TENANT, (state, action) => {
        // Clear the cache if the tenant changes
        if (action.instance) {
          state.listFetched = null;
        }
      });
  }
});

export default slice.reducer;
